import { Controller } from '@hotwired/stimulus'
import type { ConfirmationRequest } from '../lib/confirmation'
import { lockPage, unlockPage } from '../lib/page_lock'

const SLIDE_MS = 220
const EXIT_MS = 200
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export default class extends Controller<HTMLDialogElement> {
  static targets = ['title', 'message', 'accept', 'icon', 'body', 'loading']
  static values = { title: String, label: String }

  declare readonly titleTarget: HTMLElement
  declare readonly messageTarget: HTMLElement
  declare readonly acceptTarget: HTMLButtonElement
  declare readonly iconTargets: HTMLElement[]
  declare readonly bodyTarget: HTMLElement
  declare readonly loadingTarget: HTMLTemplateElement
  declare readonly titleValue: string
  declare readonly labelValue: string

  private resolve?: (answer: boolean) => void
  private form?: HTMLFormElement
  private requested: { label: string; intent: string } = { label: '', intent: 'neutral' }
  private trigger?: HTMLElement
  private dragStartY = 0
  private dragDelta: number | null = null
  private exitTimer?: ReturnType<typeof setTimeout>

  disconnect() {
    this.finish(false, false)
  }

  show(event: CustomEvent<ConfirmationRequest>) {
    if (event.defaultPrevented) return
    event.preventDefault()
    if (this.resolve || document.documentElement.hasAttribute('data-turbo-preview')) {
      event.detail.resolve(false)
      return
    }

    const { message, title, label, intent, icon, src, form, resolve } = event.detail
    this.resolve = resolve
    // Cutting the last exit short keeps its trigger: focus is still inside the dialog.
    if (this.element.open) this.close(false)
    else this.trigger = document.activeElement as HTMLElement
    // After the close: closing clears the previous request's form.
    this.form = form
    this.titleTarget.textContent = title || this.titleValue
    this.messageTarget.textContent = message
    this.requested = { label: label || this.labelValue, intent: intent === 'destructive' ? 'destructive' : 'neutral' }
    this.acceptTarget.textContent = this.requested.label
    this.element.dataset.intent = this.requested.intent
    const name = icon || (intent === 'destructive' ? 'trash' : 'question')
    this.iconTargets.forEach((target) => { target.hidden = target.dataset.icon !== name })
    this.loadBody(src)
    this.element.showModal()
    lockPage(this.element)
    // Focus the dialog, not Cancel: browsers ring a scripted button focus after a click.
    this.element.focus()
  }

  accept() {
    if (this.acceptTarget.disabled) return
    this.carryBodyFields()
    this.finish(true)
  }

  // Enter inside the body form answers the dialog; the form itself never submits.
  submitBody(event: Event) {
    event.preventDefault()
    this.accept()
  }

  // Runs when the body frame loads and on every change inside it. Panels marked
  // data-confirmation-when="x" show (and their fields enable) only while the
  // choice marked data-confirmation-choice="x" is checked; a checked choice may
  // retitle the accept button and switch the dialog's intent. Accept waits on
  // the body form's own validity, so a required select gates it without script.
  sync() {
    const body = this.bodyTarget
    body.querySelectorAll<HTMLElement>('[data-confirmation-when]').forEach((panel) => {
      const on = !!body.querySelector(`[data-confirmation-choice="${panel.dataset.confirmationWhen}"]:checked`)
      panel.hidden = !on
      panel.querySelectorAll<HTMLInputElement>('input, select, textarea').forEach((field) => { field.disabled = !on })
    })
    const choice = body.querySelector<HTMLInputElement>('[data-confirmation-choice]:checked')
    this.acceptTarget.textContent = choice?.dataset.confirmationLabel || this.requested.label
    const intent = choice?.dataset.confirmationIntent || this.requested.intent
    this.element.dataset.intent = intent === 'destructive' ? 'destructive' : 'neutral'
    const form = body.querySelector('form')
    this.acceptTarget.disabled = !!form && !form.checkValidity()
  }

  private loadBody(src?: string) {
    const body = this.bodyTarget
    body.innerHTML = ''
    body.removeAttribute('src')
    this.acceptTarget.disabled = false
    if (!src) return
    this.acceptTarget.disabled = true
    body.append(this.loadingTarget.content.cloneNode(true))
    body.setAttribute('src', src)
  }

  // The body's answers ride on the submission Turbo is holding. Turbo built
  // that request's FormData before it asked for confirmation, so hidden inputs
  // added now would be ignored; the fields join the fetch body as it is sent.
  private carryBodyFields() {
    const form = this.form
    const body = this.bodyTarget.querySelector('form')
    if (!form || !body) return
    const fields = new FormData(body)
    form.addEventListener('turbo:before-fetch-request', (event) => {
      const { fetchOptions, url } = (event as CustomEvent).detail
      const target = fetchOptions?.body instanceof FormData || fetchOptions?.body instanceof URLSearchParams ? fetchOptions.body : url?.searchParams
      fields.forEach((value, name) => { if (typeof value === 'string') target?.append(name, value) })
    }, { once: true })
  }

  cancel(event?: Event) {
    if (event?.type === 'cancel') event.preventDefault()
    // Turbo is snapshotting or leaving the page: no exit to watch.
    this.finish(false, !event?.type.startsWith('turbo:'))
  }

  closed() {
    if (!this.element.open) this.finish(false, false)
  }

  backdrop(event: MouseEvent) {
    if (event.target === this.element) this.cancel()
  }

  keydown(event: KeyboardEvent) {
    // A confirmation over a drawer must not close the drawer as well.
    if (event.key === 'Escape') event.stopPropagation()
  }

  // ---- swipe-to-dismiss, as on the mobile menu sheet (mobile_menu_controller) ----

  dragStart(event: PointerEvent) {
    if (!this.element.open) return
    this.dragStartY = event.clientY
    this.dragDelta = 0
    ;(event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId)
    // An entry animation still running would override the inline transform.
    this.element.getAnimations?.().forEach((animation) => animation.finish())
    this.element.style.transition = 'none'
  }

  dragMove(event: PointerEvent) {
    if (this.dragDelta === null) return
    // Downward only; the sheet never lifts above its resting place.
    this.dragDelta = Math.max(0, event.clientY - this.dragStartY)
    this.element.style.transform = `translateY(${this.dragDelta}px)`
  }

  // The browser ended the gesture, not the user: snap back, never dismiss.
  dragCancel() {
    if (this.dragDelta === null) return
    this.dragDelta = null
    this.element.style.transition = ''
    this.element.style.transform = ''
  }

  dragEnd() {
    if (this.dragDelta === null) return
    const dismiss = this.dragDelta > Math.min(this.element.offsetHeight * 0.3, 140)
    this.dragDelta = null
    this.element.style.transition = ''
    if (!dismiss) {
      this.element.style.transform = ''
      return
    }
    if (reducedMotion()) {
      this.finish(false, false)
      return
    }
    // Slide the rest of the way down from the dragged position, then answer no.
    const resolve = this.resolve
    this.element.style.transform = 'translateY(100%)'
    this.element.dataset.closing = 'drag' // fades the backdrop alongside
    setTimeout(() => { if (this.resolve === resolve) this.finish(false, false) }, SLIDE_MS)
  }

  // Answers at once, so the action does not wait on the exit animation.
  private finish(answer: boolean, animate = true) {
    const resolve = this.resolve
    this.resolve = undefined
    unlockPage(this.element)
    if (!animate || !this.element.open || reducedMotion()) {
      this.close()
    } else if (!this.exitTimer) {
      this.element.dataset.closing = ''
      this.exitTimer = setTimeout(() => this.close(), EXIT_MS)
    }
    resolve?.(answer)
  }

  private close(restoreFocus = true) {
    clearTimeout(this.exitTimer)
    this.exitTimer = undefined
    delete this.element.dataset.closing
    if (this.element.open) this.element.close()
    this.element.style.transform = ''
    this.loadBody()
    this.form = undefined
    if (!restoreFocus) return
    // Only once closed: the page behind an open modal cannot take focus.
    if (this.trigger?.isConnected) this.trigger.focus()
    this.trigger = undefined
  }
}
